import type { UseInfiniteQueryResult } from '@tanstack/react-query';
import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import { errorMessage } from '../lib/api';
import type { SubscriptionStatus, VideoStatus } from '../queries/admin';
import { SearchIcon } from './icons';
import { Badge, Button, ErrorState, Loading, Spinner, cx, type Tone } from './ui';

/**
 * The pieces an admin table is made of. Dense on purpose: this is a tool for
 * people who look at it every day, and whitespace that helps a first-time
 * visitor costs everyone else a scroll.
 */

// ---------------------------------------------------------------------------
// Tiles
// ---------------------------------------------------------------------------

export function Stat({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  tone?: Tone;
}) {
  return (
    <div className="card px-4 py-3.5">
      <p className="text-micro uppercase tracking-[0.1em] text-chalk-faint">{label}</p>
      <p className={cx('tnum mt-1.5 text-h2 text-chalk', tone === 'danger' && 'text-danger', tone === 'caution' && 'text-caution')}>
        {value}
      </p>
      {hint ? <p className="mt-1 text-caption text-chalk-dim">{hint}</p> : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tables
// ---------------------------------------------------------------------------

export function Table({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cx('card overflow-x-auto', className)}>
      <table className="w-full border-collapse text-body">{children}</table>
    </div>
  );
}

export function Th({ children, className, align = 'left' }: { children?: ReactNode; className?: string; align?: 'left' | 'right' }) {
  return (
    <th
      scope="col"
      className={cx(
        'whitespace-nowrap border-b border-hairline px-3 py-2 text-micro font-medium uppercase tracking-[0.08em] text-chalk-faint',
        align === 'right' ? 'text-right' : 'text-left',
        className,
      )}
    >
      {children}
    </th>
  );
}

export function Tr({ children, onClick, className }: { children: ReactNode; onClick?: () => void; className?: string }) {
  return (
    <tr
      onClick={onClick}
      className={cx(
        'border-b border-hairline last:border-b-0',
        onClick && 'cursor-pointer transition-colors duration-120 hover:bg-slate-800/60',
        className,
      )}
    >
      {children}
    </tr>
  );
}

export function Td({
  children,
  className,
  align = 'left',
  ...rest
}: ComponentPropsWithoutRef<'td'> & { align?: 'left' | 'right' }) {
  return (
    <td
      className={cx('px-3 py-2 align-middle', align === 'right' ? 'tnum text-right' : 'text-left', className)}
      {...rest}
    >
      {children}
    </td>
  );
}

/** A table body with the three states every list has, plus the rows. */
export function Rows({
  query,
  columns,
  empty,
  children,
}: {
  query: UseInfiniteQueryResult<unknown, unknown>;
  columns: number;
  empty: string;
  children: ReactNode;
}) {
  if (query.isPending) {
    return (
      <tbody>
        <tr>
          <td colSpan={columns}>
            <Loading />
          </td>
        </tr>
      </tbody>
    );
  }
  if (query.isError) {
    return (
      <tbody>
        <tr>
          <td colSpan={columns} className="p-3">
            <ErrorState message={errorMessage(query.error)} onRetry={() => void query.refetch()} />
          </td>
        </tr>
      </tbody>
    );
  }
  const pages = (query.data as { pages: Array<{ items: unknown[] }> } | undefined)?.pages ?? [];
  const total = pages.reduce((sum, page) => sum + page.items.length, 0);
  if (total === 0) {
    return (
      <tbody>
        <tr>
          <td colSpan={columns} className="px-3 py-10 text-center text-body text-chalk-faint">
            {empty}
          </td>
        </tr>
      </tbody>
    );
  }
  return <tbody>{children}</tbody>;
}

export function LoadMore({ query }: { query: UseInfiniteQueryResult<unknown, unknown> }) {
  if (!query.hasNextPage) return null;
  return (
    <div className="mt-3 flex justify-center">
      <Button onClick={() => void query.fetchNextPage()} busy={query.isFetchingNextPage}>
        Load more
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Controls
// ---------------------------------------------------------------------------

export function SearchBox({
  value,
  onChange,
  placeholder,
  busy = false,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  busy?: boolean;
}) {
  return (
    <label className="relative block w-full sm:max-w-sm">
      <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-chalk-faint">
        {busy ? <Spinner size={14} /> : <SearchIcon size={14} />}
      </span>
      <input
        type="search"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        className="h-9 w-full rounded border border-hairline bg-slate-900 pl-9 pr-3 text-body text-chalk placeholder:text-chalk-faint transition-colors duration-120 hover:border-slate-600 focus:border-signal focus:outline-none"
      />
    </label>
  );
}

/** A row of mutually exclusive filters, like a segmented control. */
export function Segmented<T extends string>({
  value,
  onChange,
  options,
}: {
  value: T;
  onChange: (value: T) => void;
  options: Array<{ value: T; label: string }>;
}) {
  return (
    <div role="radiogroup" className="inline-flex rounded border border-hairline bg-slate-900 p-0.5">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          role="radio"
          aria-checked={value === option.value}
          onClick={() => onChange(option.value)}
          className={cx(
            'rounded-sm px-2.5 py-1 text-caption transition-colors duration-120',
            value === option.value ? 'bg-slate-700 text-chalk' : 'text-chalk-dim hover:text-chalk',
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Cells
// ---------------------------------------------------------------------------

export function Identity({
  name,
  email,
  avatarUrl,
}: {
  name: string | null;
  email: string;
  avatarUrl?: string | null;
}) {
  const label = name?.trim() || email.split('@')[0];
  return (
    <span className="flex min-w-0 items-center gap-2.5">
      {avatarUrl ? (
        <img src={avatarUrl} alt="" className="h-7 w-7 shrink-0 rounded-full object-cover" />
      ) : (
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-hairline bg-slate-800 text-caption uppercase text-chalk-dim">
          {label.slice(0, 1)}
        </span>
      )}
      <span className="min-w-0">
        <span className="block truncate text-chalk">{label}</span>
        <span className="block truncate text-caption text-chalk-faint">{email}</span>
      </span>
    </span>
  );
}

/** An identifier: monospace, selectable, never wrapped. */
export function Mono({ children, className }: { children: ReactNode; className?: string }) {
  return <code className={cx('select-all whitespace-nowrap font-mono text-caption text-chalk-dim', className)}>{children}</code>;
}

const SUBSCRIPTION_TONES: Record<SubscriptionStatus, Tone | 'neutral'> = {
  active: 'go',
  trialing: 'info',
  past_due: 'caution',
  canceled: 'neutral',
  incomplete: 'neutral',
};

const SUBSCRIPTION_LABELS: Record<SubscriptionStatus, string> = {
  active: 'Active',
  trialing: 'Trial',
  past_due: 'Past due',
  canceled: 'Canceled',
  incomplete: 'No plan',
};

export function SubscriptionBadge({ status }: { status: SubscriptionStatus }) {
  return <Badge tone={SUBSCRIPTION_TONES[status] ?? 'neutral'}>{SUBSCRIPTION_LABELS[status] ?? status}</Badge>;
}

const VIDEO_TONES: Record<VideoStatus, Tone | 'neutral'> = {
  ready: 'go',
  processing: 'info',
  uploading: 'neutral',
  failed: 'danger',
};

export function VideoBadge({ status, deleted = false }: { status: VideoStatus; deleted?: boolean }) {
  if (deleted) return <Badge tone="neutral">Deleted</Badge>;
  return (
    <Badge tone={VIDEO_TONES[status] ?? 'neutral'} pulse={status === 'processing'}>
      {status}
    </Badge>
  );
}

// ---------------------------------------------------------------------------
// Detail pages
// ---------------------------------------------------------------------------

export function Facts({ children }: { children: ReactNode }) {
  return <dl className="card grid grid-cols-1 gap-x-6 gap-y-3 px-4 py-4 sm:grid-cols-2 lg:grid-cols-3">{children}</dl>;
}

export function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-micro uppercase tracking-[0.1em] text-chalk-faint">{label}</dt>
      <dd className="mt-0.5 truncate text-body text-chalk">{children ?? <span className="text-chalk-faint">none</span>}</dd>
    </div>
  );
}

export function Section({ title, count, children, action }: { title: string; count?: number; children: ReactNode; action?: ReactNode }) {
  return (
    <section className="mt-8">
      <div className="mb-3 flex items-center justify-between gap-3">
        <h2 className="text-h4 text-chalk">
          {title}
          {count !== undefined ? <span className="tnum ml-2 text-body text-chalk-faint">{count.toLocaleString()}</span> : null}
        </h2>
        {action}
      </div>
      {children}
    </section>
  );
}

export function ExternalLink({ href, children, className }: { href: string; children: ReactNode; className?: string }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(event) => event.stopPropagation()}
      className={cx('text-signal underline-offset-2 hover:underline', className)}
    >
      {children}
    </a>
  );
}
